
import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft } from "lucide-react";
import { calculateDistance, formatDistance } from "@/utils/distance";
import { Button } from "@/components/ui/button";
import { geocodeAllAddresses } from "@/utils/geocoding";

interface Meal {
  id: string;
  name: string;
  price: number;
  image?: string;
  latitude?: number;
  longitude?: number;
}

const Map = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const [mapboxToken, setMapboxToken] = useState<string>("");
  const [meals, setMeals] = useState<Meal[]>([]);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleGeocodeAddresses = async () => {
    try {
      setIsGeocoding(true);
      const result = await geocodeAllAddresses();
      toast({
        title: "Geocoding Complete",
        description: `Processed ${result.details?.total || 0} addresses. Success: ${result.details?.successful || 0}, Failed: ${result.details?.failed || 0}`,
      });
    } catch (error) {
      console.error('Error geocoding addresses:', error);
      toast({
        title: "Geocoding Error",
        description: "Failed to geocode addresses. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeocoding(false);
    }
  };

  useEffect(() => {
    const fetchMapboxToken = async () => {
      try {
        const { data, error: functionError } = await supabase.functions.invoke('get-mapbox-token', {
          method: 'GET',
        });

        if (functionError) {
          throw new Error('Failed to fetch Mapbox token');
        }

        if (!data?.token) {
          throw new Error('No token received');
        }

        setMapboxToken(data.token);
      } catch (error) {
        console.error("Error fetching Mapbox token:", error);
        setError("Failed to load map. Please try again later.");
        toast({
          title: "Error",
          description: "Failed to load map. Please try again later.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchMapboxToken();
  }, [toast]);

  useEffect(() => {
    const fetchMeals = async () => {
      try {
        const { data: mealsData, error: mealsError } = await supabase
          .from("meals")
          .select("id, name, price, image, latitude, longitude")
          .eq("status", "active");

        if (mealsError) throw mealsError;
        
        const validMeals = mealsData?.filter(meal => meal.latitude && meal.longitude) || [];
        const invalidMeals = mealsData?.filter(meal => !meal.latitude || !meal.longitude) || [];
        
        invalidMeals.forEach(meal => {
          console.warn(`Meal ${meal.id} is missing coordinates and won't be displayed on the map`, meal);
        });

        setMeals(validMeals);
      } catch (error) {
        console.error("Error fetching meals:", error);
        toast({
          title: "Error",
          description: "Failed to load meals data.",
          variant: "destructive",
        });
      }
    };

    fetchMeals();
  }, [toast]);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation([position.coords.longitude, position.coords.latitude]);
      },
      (error) => {
        console.error("Error getting location:", error);
      }
    );
  }, []);

  useEffect(() => {
    if (!mapboxToken || !mapContainer.current || map.current || !meals.length) return;

    try {
      mapboxgl.accessToken = mapboxToken;

      const newMap = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/streets-v12",
        center: [-111.8389, 33.4152],
        zoom: 11,
        attributionControl: false,
      });

      newMap.addControl(new mapboxgl.NavigationControl(), 'top-right');

      newMap.on('load', () => {
        meals.forEach(meal => {
          if (!meal.latitude || !meal.longitude) return;

          const markerEl = document.createElement('div');
          markerEl.className = 'custom-marker';
          markerEl.innerHTML = `
            <div class="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white cursor-pointer">
              <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="12" r="10" />
              </svg>
            </div>
          `;

          const popup = new mapboxgl.Popup({ 
            offset: 25,
            closeButton: false,
            maxWidth: '300px',
            className: 'custom-popup'
          }).setHTML(`
            <div class="p-4 max-w-sm relative">
              <button class="absolute top-2 right-2 p-1 bg-white rounded-full shadow-sm hover:bg-gray-100 transition-colors" onclick="this.parentElement.parentElement.remove()">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              <img src="${meal.image || '/placeholder.svg'}" alt="${meal.name}" class="w-full h-32 object-cover rounded mb-3"/>
              <h3 class="font-semibold text-lg mb-2">${meal.name}</h3>
              <p class="text-sm text-gray-600 mb-2">$${meal.price}</p>
              ${userLocation ? `
                <p class="text-sm text-gray-600 mb-3">
                  ${formatDistance(
                    calculateDistance(
                      userLocation[1],
                      userLocation[0],
                      meal.latitude,
                      meal.longitude
                    )
                  )}
                </p>
              ` : ''}
              <button 
                onclick="window.location.href='/meal/${meal.id}'" 
                class="w-full bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 transition-colors"
              >
                View Details
              </button>
            </div>
          `);

          const marker = new mapboxgl.Marker(markerEl)
            .setLngLat([meal.longitude, meal.latitude])
            .setPopup(popup)
            .addTo(newMap);

          markers.current.push(marker);
        });
      });

      map.current = newMap;

    } catch (mapError) {
      console.error("Error initializing map:", mapError);
      setError("Failed to initialize map. Please try again later.");
      toast({
        title: "Error",
        description: "Failed to initialize map. Please try again later.",
        variant: "destructive",
      });
    }

    return () => {
      markers.current.forEach(marker => marker.remove());
      markers.current = [];
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [mapboxToken, meals, toast, userLocation]);

  if (isLoading) {
    return (
      <div className="w-full h-[calc(100vh-4rem)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-[calc(100vh-4rem)] flex items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-[calc(100vh-4rem)] relative">
      <div className="absolute top-4 left-4 z-10 flex gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="bg-background/80 backdrop-blur-sm"
        >
          <ArrowLeft className="h-6 w-6" />
        </Button>
        <Button
          onClick={handleGeocodeAddresses}
          disabled={isGeocoding}
          className="bg-background/80 backdrop-blur-sm"
        >
          {isGeocoding ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : null}
          {isGeocoding ? "Processing..." : "Geocode Addresses"}
        </Button>
      </div>
      <div ref={mapContainer} className="w-full h-full" />
      <style>
        {`
          .mapboxgl-popup-content {
            padding: 0;
            border-radius: 8px;
          }
          .custom-popup {
            max-width: 320px !important;
          }
          .mapboxgl-ctrl-logo {
            display: none !important;
          }
          .mapboxgl-ctrl-attrib {
            display: none !important;
          }
        `}
      </style>
    </div>
  );
};

export default Map;
